/**
 * Rounding policies, the master lists behind the policy wizard, and a
 * deterministic simulation result.
 */

import type {
  PolicyLookupOption,
  RoundingPolicy,
  RoundingPolicyMasters,
  RoundingPolicySimulationDetail,
} from '@/services/tenant/policy-services';

const option = (id: number, name: string): PolicyLookupOption => ({ id, name });

export const TOUR_POLICY_MASTERS: RoundingPolicyMasters = {
  roundingTypes: [
    option(1, 'Half_Up'),
    option(2, 'Half_Down'),
    option(3, 'Floor'),
    option(4, 'Ceiling'),
    option(5, 'Truncate'),
  ],
  fieldLevels: [option(1, 'NoofShare'), option(2, 'Unit Price')],
  stageLevels: [
    option(1, 'ContigentAward'),
    option(2, 'VestedAward'),
    option(3, 'FinalAward'),
    option(4, 'CreditingAward'),
  ],
  // A sub-stage id repeats across stages, so each entry carries its own stage.
  subStageLevels: [
    {
      subStageID: 5,
      subStageName: 'AwardShare',
      subStageDescription: 'Award Share',
      stageID: 1,
    },
    {
      subStageID: 5,
      subStageName: 'AwardShare',
      subStageDescription: 'Award Share',
      stageID: 3,
    },
    {
      subStageID: 7,
      subStageName: 'VestedShare',
      subStageDescription: 'Vested Share',
      stageID: 3,
    },
  ],
  consumerTypes: [option(1, 'DashBoard'), option(2, 'Report')],
  consumerSubTypes: [
    {
      subTypeID: 11,
      subTypeName: 'AdminDashBoard',
      subTypeDescription: 'Admin DashBoard',
      typeID: 1,
    },
    {
      subTypeID: 12,
      subTypeName: 'ParticipantDashBoard',
      subTypeDescription: 'Participant DashBoard',
      typeID: 1,
    },
    {
      subTypeID: 21,
      subTypeName: 'FinalAwardReport',
      subTypeDescription: 'Final Award Report',
      typeID: 2,
    },
    {
      subTypeID: 22,
      subTypeName: 'VestingForecastReport',
      subTypeDescription: 'Vesting Forecast Report',
      typeID: 2,
    },
  ],
  reconcilStrategyTypes: [
    option(1, 'First Row'),
    option(2, 'Last Row'),
    option(3, 'Largest Row'),
  ],
  // Presentation fields, each declared against the sub-type that exposes it.
  consumerTypeFieldMappings: [
    {
      fieldID: 1,
      fieldName: 'NoofShare',
      fieldDescription: 'NoofShare',
      consumerSubTypeID: 11,
    },
    {
      fieldID: 2,
      fieldName: 'UnitPrice',
      fieldDescription: 'Unit Price',
      consumerSubTypeID: 11,
    },
    {
      fieldID: 3,
      fieldName: 'NoofShare',
      fieldDescription: 'NoofShare',
      consumerSubTypeID: 12,
    },
    {
      fieldID: 4,
      fieldName: 'closingPrice',
      fieldDescription: 'Closing Price (S$)',
      consumerSubTypeID: 21,
    },
    {
      fieldID: 5,
      fieldName: 'NoofShare',
      fieldDescription: 'NoofShare',
      consumerSubTypeID: 22,
    },
  ],
};

const businessModel = (precision: number) => ({
  businessFieldLevels: [
    {
      fieldLevelId: 1,
      fieldLevelName: 'NoofShare',
      roundingTypeId: 1,
      roundingTypeName: 'Half_Up',
      precision,
    },
    {
      fieldLevelId: 2,
      fieldLevelName: 'Unit Price',
      roundingTypeId: 5,
      roundingTypeName: 'Truncate',
      precision: 4,
    },
  ],
  businessStageLevels: [
    {
      stageLevelId: 1,
      stageLevelName: 'ContigentAward',
      subStageId: 5,
      subStageName: 'Award Share',
      roundingTypeId: 3,
      roundingTypeName: 'Floor',
      precision: 0,
    },
    {
      stageLevelId: 3,
      stageLevelName: 'FinalAward',
      subStageId: 7,
      subStageName: 'Vested Share',
      roundingTypeId: 1,
      roundingTypeName: 'Half_Up',
      precision: 0,
    },
  ],
});

const presentationModel = () => [
  {
    consumerTypeId: 1,
    consumerTypeName: 'DashBoard',
    consumerTypeSubId: 11,
    consumerTypeSubName: 'Admin DashBoard',
    presentationSubTypes: [
      {
        presentationFieldID: 1,
        presentationFieldName: 'NoofShare',
        roundingId: 1,
        roundingName: 'Half_Up',
        precision: 0,
      },
      {
        presentationFieldID: 2,
        presentationFieldName: 'Unit Price',
        roundingId: 1,
        roundingName: 'Half_Up',
        precision: 2,
      },
    ],
  },
  {
    consumerTypeId: 2,
    consumerTypeName: 'Report',
    consumerTypeSubId: 21,
    consumerTypeSubName: 'Final Award Report',
    presentationSubTypes: [
      {
        presentationFieldID: 4,
        presentationFieldName: 'Closing Price (S$)',
        roundingId: 3,
        roundingName: 'Floor',
        precision: 0,
      },
    ],
  },
];

const reconciliationModel = () => ({
  isRaw: true,
  adjustmentStrategyId: 3,
  adjustmentStrategyName: 'Largest Row',
  maxAdjustPrecision: 2,
  isEquity: true,
  isCash: false,
  isReconRawValue: true,
  isReconAdjustValue: true,
  isReconDeltaValue: false,
});

interface PolicySeed {
  policyId: number;
  policyName: string;
  planTypeId: number;
  planTypeName: string;
  statusId: number;
  statusName: string;
  effectiveDate: string;
  precision: number;
  txnId?: string | null;
  txnStatus?: string | null;
  isDraft?: boolean;
}

const POLICY_SEEDS: PolicySeed[] = [
  {
    policyId: 601,
    policyName: 'PSP Standard Rounding 2025',
    planTypeId: 301,
    planTypeName: 'Performance Share Plan',
    statusId: 1,
    statusName: 'Active',
    effectiveDate: '2025-01-01T00:00:00',
    precision: 0,
  },
  {
    policyId: 602,
    policyName: 'RSP Whole Share Rounding',
    planTypeId: 302,
    planTypeName: 'Restricted Share Plan',
    statusId: 1,
    statusName: 'Active',
    effectiveDate: '2025-01-01T00:00:00',
    precision: 0,
  },
  {
    policyId: 603,
    policyName: 'ESOS Fractional Rounding',
    planTypeId: 303,
    planTypeName: 'Employee Share Option Scheme',
    statusId: 1,
    statusName: 'Active',
    effectiveDate: '2024-07-01T00:00:00',
    precision: 2,
  },
  {
    policyId: 604,
    policyName: 'PSP Rounding 2026 (Proposed)',
    planTypeId: 301,
    planTypeName: 'Performance Share Plan',
    statusId: 3,
    statusName: 'Pending Checker Approval',
    effectiveDate: '2026-01-01T00:00:00',
    precision: 0,
    txnId: 'TOUR-TXN-POL-0001',
    txnStatus: 'Pending Checker Approval',
  },
  {
    policyId: 605,
    policyName: 'Phantom Cash Rounding (Draft)',
    planTypeId: 304,
    planTypeName: 'Phantom Cash Plan',
    statusId: 2,
    statusName: 'Draft',
    effectiveDate: '2026-04-01T00:00:00',
    precision: 2,
    isDraft: true,
  },
];

export const TOUR_ROUNDING_POLICIES: readonly RoundingPolicy[] = Object.freeze(
  POLICY_SEEDS.map(seed => ({
    policyId: seed.policyId,
    policyName: seed.policyName,
    policyVersion: 1,
    planTypeId: seed.planTypeId,
    planTypeName: seed.planTypeName,
    isEquityPlan: seed.planTypeId !== 304,
    countryId: 1,
    countryName: 'Singapore',
    effectiveDate: seed.effectiveDate,
    policyStatusId: seed.statusId,
    policyStatusName: seed.statusName,
    createdBy: 9006,
    createdDate: '2024-12-02T09:00:00',
    lastUpdatedBy: 9001,
    lastUpdatedDate: '2025-02-14T11:25:00',
    policyBusinessModel: businessModel(seed.precision),
    policyPresentationModel: presentationModel(),
    policyReconciliationModel: reconciliationModel(),
    isDraft: seed.isDraft ?? false,
    txnId: seed.txnId ?? null,
    txnStatus: seed.txnStatus ?? null,
  }))
);

export const TOUR_PRIMARY_POLICY = TOUR_ROUNDING_POLICIES[0];

/**
 * The policies GET /List offers to simulate against.
 *
 * The resource no longer exposes a purpose-built /Simulation list, so the
 * simulation picker reads whole policies and derives its stage and field choices
 * from the rules each one carries.
 */
export const TOUR_ACTIVE_POLICIES: readonly RoundingPolicy[] = Object.freeze(
  TOUR_ROUNDING_POLICIES.filter(policy => policy.policyStatusName === 'Active')
);

const ROUNDING_FUNCTIONS: Record<
  string,
  (value: number, dp: number) => number
> = {
  Half_Up: (value, dp) => {
    const factor = 10 ** dp;
    return Math.round(value * factor) / factor;
  },
  Half_Down: (value, dp) => {
    const factor = 10 ** dp;
    return -Math.round(-value * factor) / factor;
  },
  Floor: (value, dp) => {
    const factor = 10 ** dp;
    return Math.floor(value * factor) / factor;
  },
  Ceiling: (value, dp) => {
    const factor = 10 ** dp;
    return Math.ceil(value * factor) / factor;
  },
  Truncate: (value, dp) => {
    const factor = 10 ** dp;
    return Math.trunc(value * factor) / factor;
  },
};

/**
 * Run a value through a sample policy's rules.
 *
 * The arithmetic is done here rather than canned, so the simulation answers
 * whatever number the user types during the tour — the point of the screen —
 * while staying entirely local.
 */
export const simulateTourPolicy = (params: {
  policyId: number | string;
  stageLevelId?: number | string;
  fieldLevelId?: number | string;
  inputValue: number | string;
}): RoundingPolicySimulationDetail[] => {
  const policy =
    TOUR_ROUNDING_POLICIES.find(
      item => String(item.policyId) === String(params.policyId)
    ) ?? TOUR_PRIMARY_POLICY;

  const input = Number(params.inputValue);
  if (!Number.isFinite(input)) return [];

  const stageFilter = Number(params.stageLevelId ?? 0);
  const fieldFilter = Number(params.fieldLevelId ?? 0);

  const fieldRows = policy.policyBusinessModel.businessFieldLevels
    .filter(rule => !fieldFilter || rule.fieldLevelId === fieldFilter)
    .map((rule, index) => ({
      id: index + 1,
      policyName: policy.policyName,
      ruleName: `Field level - ${rule.fieldLevelName}`,
      stageLevelName: '',
      fieldLevelName: rule.fieldLevelName,
      inputValue: input,
      roundedValue: (
        ROUNDING_FUNCTIONS[rule.roundingTypeName] ?? ROUNDING_FUNCTIONS.Half_Up
      )(input, rule.precision),
      precisionApplied: String(rule.precision),
    }));

  const stageRows = policy.policyBusinessModel.businessStageLevels
    .filter(rule => !stageFilter || rule.stageLevelId === stageFilter)
    .map((rule, index) => ({
      id: fieldRows.length + index + 1,
      policyName: policy.policyName,
      ruleName: `Stage level - ${rule.stageLevelName}`,
      stageLevelName: rule.stageLevelName,
      fieldLevelName: '',
      inputValue: input,
      roundedValue: (
        ROUNDING_FUNCTIONS[rule.roundingTypeName] ?? ROUNDING_FUNCTIONS.Half_Up
      )(input, rule.precision),
      precisionApplied: String(rule.precision),
    }));

  return [...fieldRows, ...stageRows];
};
